export type uuid = string
export type IdeaType = "idea" | "goal" | "fleeting"

export type EditOriginator = "editor" | "model"

export type IdeaFieldName =
  | "state"
  | "colour"
  | "memo"
  | "title"
  | "content"
  | "location"
  | "sourceType"
  | "sourceId"
  | "connections"

export type ID = number | string

export type FleetingIdea = {
  id: ID
  content: string
  sourceId?: ID
  createdAt: Date
}

export type SourceType = "self" | "web"
export type SourceStatus = "new" | "queued" | "read"

export type WebSourceFieldName =
  | "url"
  | "name"
  | "userProvidedName"
  | "scannedName"
  | "author"
  | "publicationDate"
  | "type"
  | "metadataError"
  | "scannedAt"
  | "iframeForbidden"
  | "hasStoredContent"
  | "contentFileContentType"
  | "hasStoredMarkdown"
  | "status"
  | "goalId"
  | "literatureNotes"
  | "summary"
  | "lastReadAt"
  | "lastUsedAt"

export type WebSourceFields = {
  type?: string | null
  author?: string | null
  name?: string | null
  userProvidedName?: string | null
  scannedName?: string | null
  publicationDate?: Date | null
  scannedAt?: Date | null
  metadataError?: string | null
  status?: SourceStatus | null
  url?: string | null
  goalId?: ID | null
  literatureNotes?: string | null
  summary?: string | null
  iframeForbidden?: boolean | null
  hasStoredContent?: boolean | null
  contentFileContentType?: string | null
  hasStoredMarkdown?: boolean | null
  lastUsedAt?: Date
}

export type WebSource = {
  id: ID
  url?: string | null
  name?: string
  userProvidedName?: string
  scannedName?: string
  author?: string
  publicationDate?: Date
  type?: string
  status: SourceStatus
  createdAt: Date
  updatedAt: Date
  lastUsedAt?: Date
  lastReadAt?: Date
  scannedAt?: Date
  metadataError?: string
  goalId?: ID
  literatureNotes?: string
  summary?: string | null
  iframeForbidden?: boolean | null
  hasStoredContent?: boolean | null
  contentFileContentType?: string | null
  hasStoredMarkdown?: boolean | null
}

export type EditableIdeaFields = {
  colour?: HexColor
  memo?: string
  title?: string
  content?: string
  lastViewedAt?: Date | null
  location?: Location
  sourceType?: null | SourceType
  sourceId?: null | ID
  connections?: ID[]
  updatedAt?: Date
  editedAt?: Date
}

export type Idea = {
  id: ID
  type: IdeaType
  colour: HexColor
  memo: string
  title: string
  content: string
  location: Location
  sourceType: null | SourceType
  sourceId: null | ID
  connections: ID[]
  createdAt: Date
  updatedAt: Date
  editedAt: Date
  lastViewedAt: Date | null
}

export const ideaWidth = (idea: Idea | null | undefined): number => {
  if (!idea) return 1
  return idea.type === "goal" ? 2 : 1
}

export const ideaHeight = (idea: Idea | null | undefined): number => {
  if (!idea) return 1
  return idea.type === "goal" ? 2 : 1
}

export const ideaIsEmpty = (idea: Idea): boolean => {
  return (
    (idea.title == "" || idea.title == null || idea.title == undefined) &&
    (idea.content == "" ||
      idea.content == null ||
      idea.content == undefined ||
      idea.content == "<p></p>")
  )
}

export type Location = {
  gx: number
  gy: number
}

export type LocationDiff = {
  dx: number
  dy: number
}

export type HexColor = `#${string}` & { length: 7 }

export type ProjectStatus = "in_progress" | "completed" | "dropped"

export type Project = {
  id: ID
  name: string
  status: ProjectStatus
  completedAt?: Date
  goalId?: ID | null
  ideaIds: ID[]
  createdAt: Date
  updatedAt: Date
}

export type EditableProjectFields = {
  name?: string
  status?: ProjectStatus
  completedAt?: Date
  goalId?: ID | null
  ideaIds?: ID[]
}

export type ProjectApiData = {
  id: ID
  name: string
  status: ProjectStatus
  completed_at?: string
  goal_id?: ID | null
  idea_ids: ID[]
  created_at: string
  updated_at: string
  clock: number
}
